import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import { fmtDateTime } from '../../utils/dateFormat';
import useAuthStore from '../../store/authStore';
import { confirm } from '../../components/ConfirmDialog';
import { canFiscalRelay, fiscalForward } from '../../utils/printService';
import { posCardCharge } from '../../utils/tapxCharge';
import { useFoodDeliveryInfo } from '../../utils/foodDelivery';

const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Baku' });

const METHOD_LABEL = {
    nagd:  { label: 'Nağd',  cls: 'bg-emerald-100 text-emerald-700' },
    kart:  { label: 'POS',   cls: 'bg-blue-100 text-blue-700' },
    hesab: { label: 'Hesab', cls: 'bg-purple-100 text-purple-700' },
    delivery: { label: '🛵 Delivery', cls: 'bg-orange-100 text-orange-700' },
    mixed: { label: 'Qarışıq', cls: 'bg-amber-100 text-amber-700' },
    bolunmus: { label: 'Bölünmüş', cls: 'bg-amber-100 text-amber-700' },
};

const MethodBadge = ({ method }) => {
    const m = METHOD_LABEL[method] || { label: method, cls: 'bg-gray-100 text-gray-600' };
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${m.cls}`}>
            {m.label}
        </span>
    );
};

const fmt = (v) => Number(v || 0).toFixed(2);

const TABS = [
    { key: 'orders',  label: '🍽️ Restoran',   icon: '🍽️' },
    { key: 'rentals', label: '🏠 Evlər',        icon: '🏠' },
    { key: 'satis',   label: '📦 Satış',         icon: '📦' },
    { key: 'search',  label: '🔍 Axtarış',       icon: '🔍' },
];

export default function PaymentChange() {
    const navigate = useNavigate();
    const [tab,    setTab]    = useState('orders');
    const [from,   setFrom]   = useState(todayStr());
    const [to,     setTo]     = useState(todayStr());
    const [method, setMethod] = useState('all');  // nagd | kart | all
    const foodDelivery = useFoodDeliveryInfo().tenant; // «Food delivery» növü (PRO + superadmin tick)
    const [type,   setType]   = useState('all');  // for satis: nagd | pos | all

    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);

    // Search tab state
    const [searchQ,      setSearchQ]      = useState('');
    const [searchMethod, setSearchMethod] = useState('all');
    const [searchFrom,   setSearchFrom]   = useState('');
    const [searchTo,     setSearchTo]     = useState('');
    const [searchRows,   setSearchRows]   = useState([]);
    const [searching,    setSearching]    = useState(false);

    // Change modal
    const [changeModal, setChangeModal] = useState(null); // { source, record, currentMethod }
    const [newMethod,   setNewMethod]   = useState('');
    const [saving,      setSaving]      = useState(false);

    // Satis edit state
    const [satisEdit, setSatisEdit] = useState(null); // { id, nagd, pos }

    // Refund (geri qaytar) state
    const { user } = useAuthStore();
    const isAdmin = ['admin', 'superadmin'].includes(String(user?.role || '').toLowerCase()) || !!user?.is_manager;
    const [refunding, setRefunding] = useState(false);
    const [tablePopup, setTablePopup] = useState(null); // { orderId, tables: [{id,name}] }
    const [refundChoice, setRefundChoice] = useState(null); // { orderId } — kassadan/kassasız seçimi
    const [refundFiscal, setRefundFiscal] = useState({ open: false, message: '', closable: false, error: false });

    // Kassadan qaytar: SmartOne /refund (C# relay) → uğurda mövcud DB qaytarması (refund-reopen)
    const doRefundFiscal = async (orderId) => {
        if (!isAdmin) return toast.error('Yalnız admin geri qaytara bilər.');
        if (!canFiscalRelay()) return toast.error('Kassadan qaytarma yalnız Fiscal POS proqramında mümkündür.');
        setRefundFiscal({ open: true, closable: false, error: false, message: 'Qaytarma kassaya göndərilir...' });
        try {
            const prep = await api.post(`/orders/${orderId}/refund-prepare`);
            if (!prep.data?.relay) throw new Error('Kassa qaytarma sorğusu qurula bilmədi.');
            setRefundFiscal({ open: true, closable: false, error: false,
                message: prep.data.has_card_rrn
                    ? 'Kassa qaytarma qəbzini çap edir, pul orijinal karta qaytarılır...'
                    : 'Kassa qaytarma qəbzini çap edir...' });
            const fwd = await fiscalForward(prep.data.relay); // uğursuzsa xəta atır
            const raw = String(fwd.raw || '');
            let okFiscal = true;
            try { const j = JSON.parse(raw); okFiscal = j.status === 'success' && Number(j.code || 0) === 0; } catch { okFiscal = true; }
            if (!okFiscal) throw new Error('Kassa qaytarmanı qəbul etmədi.');
            setRefundFiscal({ open: true, closable: false, error: false, message: 'Qaytarma qəbul edildi. Sifariş geri açılır...' });
            // Fiskal qaytarma uğurlu → indi mövcud DB qaytarması (dəyişməz davranış)
            await doRefund(orderId, 0, true);
            setRefundFiscal({ open: false, message: '', closable: false, error: false });
        } catch (err) {
            setRefundFiscal({ open: true, closable: true, error: true,
                message: err.response?.data?.msg || err.message || 'Kassadan qaytarma baş tutmadı.' });
        }
    };

    const doRefund = async (orderId, targetTableId = 0, skipConfirm = false) => {
        if (!isAdmin) return toast.error('Yalnız admin geri qaytara bilər.');
        if (!targetTableId && !skipConfirm && !(await confirm(`Sifariş #${orderId} geri masaya açılsın? Stok, ofisiant qazancı və ödəniş geri qaytarılacaq (heç vurulmamış kimi).`))) return;
        setRefunding(true);
        try {
            const res = await api.post(`/orders/${orderId}/refund-reopen`, targetTableId ? { target_table_id: targetTableId } : {});
            if (res.data?.need_table) {
                // Orijinal masa doludur → boş masa seçimi
                setTablePopup({ orderId, tables: res.data.free_tables || [] });
                return;
            }
            toast.success(res.data?.msg || 'Geri açıldı.');
            setTablePopup(null);
            // Geri açılan sifariş həmin masada yenidən aktivdir → birbaşa onun
            // ofisiant sifariş ekranına keç (davam etdirmək/redaktə üçün).
            navigate(`/waiter/order/${res.data?.order_id || orderId}`);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Geri qaytarılmadı.');
        } finally {
            setRefunding(false);
        }
    };

    const load = useCallback(async () => {
        if (tab === 'search') return;
        setLoading(true);
        try {
            let url, params;
            if (tab === 'orders')  { url = '/payment-change/orders';  params = { from, to, method }; }
            if (tab === 'rentals') { url = '/payment-change/rentals'; params = { from, to, method }; }
            if (tab === 'satis')   { url = '/payment-change/satis';   params = { from, to, type };   }
            const res = await api.get(url, { params });
            setRows(res.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, [tab, from, to, method, type]);

    useEffect(() => { load(); }, [load]);

    const doSearch = async () => {
        setSearching(true);
        try {
            const res = await api.get('/payment-change/search', {
                params: { q: searchQ, method: searchMethod, from: searchFrom, to: searchTo }
            });
            setSearchRows(res.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Axtarış xətası.');
        } finally {
            setSearching(false);
        }
    };

    const openChange = (source, record) => {
        const current = source === 'orders'  ? record.method
                      : source === 'rentals' ? record.method
                      : null;
        setChangeModal({ source, record });
        setNewMethod(current === 'nagd' ? 'kart' : 'nagd'); // pre-select opposite
    };

    const saveChange = async () => {
        if (!changeModal) return;
        const { source, record } = changeModal;

        // Restoran: Nağd → POS düzəlişində kartı REAL çək (SmartOne relay + popup)
        if (source === 'orders' && newMethod === 'kart' && record.method === 'nagd' && canFiscalRelay()) {
            setChangeModal(null);
            setRefundFiscal({ open: true, closable: false, error: false, title: '💳 POS ödənişi', message: 'POS terminala göndərilir...' });
            try {
                // SmartOne 1 addım / Caspos TapXPhone 2 addım — posCardCharge idarə edir
                const res = await posCardCharge(Number(record.amount) || 0, 'Nağd→POS',
                    (m) => setRefundFiscal({ open: true, closable: false, error: false, title: '💳 POS ödənişi', message: m }),
                    `/payment-change/orders/${record.payment_id}/pos-charge-prepare`);
                setRefundFiscal({ open: true, closable: false, error: false, title: '💳 POS ödənişi', message: 'Qəbul edildi. Yadda saxlanılır...' });
                await api.put(`/payment-change/orders/${record.payment_id}`, { method: 'kart', fiscal_raw: res.raw, fiscal_status: res.status, tapx_rrn: res.tapx_rrn });
                setRefundFiscal({ open: false, message: '', closable: false, error: false });
                toast.success('POS ödənişi alındı — növ dəyişdirildi.');
                load(); if (searchRows.length) doSearch();
            } catch (e) {
                setRefundFiscal({ open: true, closable: true, error: true, title: '💳 POS ödənişi', message: e.response?.data?.msg || e.message || 'POS çəkiliş baş tutmadı / imtina.' });
            }
            return;
        }

        setSaving(true);
        try {
            if (source === 'orders') {
                await api.put(`/payment-change/orders/${record.payment_id}`, { method: newMethod });
            } else if (source === 'rentals') {
                await api.put(`/payment-change/rentals/${record.id}`, { method: newMethod, source: record.source || 'rental' });
            }
            toast.success('Dəyişdirildi.');
            setChangeModal(null);
            load();
            if (searchRows.length) doSearch();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSaving(false);
        }
    };

    const doRentalRefund = async (record) => {
        const ok = await confirm(
            `Bu ev ödənişini geri qaytarmaq istəyirsiniz?\n\n` +
            `Qonaq: ${record.guest_name || '—'}\nMəbləğ: ${fmt(record.amount)} ₼\n\n` +
            `• Qonağın borcu bərpa olunacaq\n• Ofisə köçən nağd (varsa) geri çəkiləcək\n• Kassa yenidən hesablanacaq`
        );
        if (!ok) return;
        try {
            await api.post(`/payment-change/rentals/${record.id}/refund`, { source: record.source || 'rental' });
            toast.success('Ödəniş geri qaytarıldı.');
            load();
            if (searchRows.length) doSearch();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const saveSatis = async () => {
        if (!satisEdit) return;
        setSaving(true);
        try {
            await api.put(`/payment-change/satis/${satisEdit.id}`, {
                nagd_amount: satisEdit.nagd,
                pos_amount:  satisEdit.pos,
            });
            toast.success('Satış yeniləndi.');
            setSatisEdit(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 transition-all bg-white';

    // Filters bar for orders / rentals
    const FiltersBar = () => (
        <div className="flex flex-wrap gap-2 mb-4">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                className={inputCls} />
            <input type="date" value={to}   onChange={e => setTo(e.target.value)}
                className={inputCls} />
            {tab !== 'satis' ? (
                <select value={method} onChange={e => setMethod(e.target.value)} className={inputCls}>
                    <option value="all">Bütün növlər</option>
                    <option value="nagd">Nağd</option>
                    <option value="kart">POS / Kart</option>
                    {tab === 'orders' && <option value="hesab">Hesab (qarışıq)</option>}
                    {tab === 'orders' && foodDelivery && <option value="delivery">Food delivery</option>}
                </select>
            ) : (
                <select value={type} onChange={e => setType(e.target.value)} className={inputCls}>
                    <option value="all">Bütün</option>
                    <option value="nagd">Nağd olanlar</option>
                    <option value="pos">POS olanlar</option>
                </select>
            )}
            <button onClick={load}
                className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all">
                Göstər
            </button>
        </div>
    );

    return (
        <Layout>
            <div>
                {/* Page title */}
                <div className="mb-5">
                    <h1 className="text-xl font-black text-gray-800">💱 Nağd / POS dəyişmə</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Yanlış daxil edilmiş ödəniş növünü düzəlt</p>
                </div>

                {/* Sub-tabs */}
                <div className="flex gap-2 mb-5 flex-wrap">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => { setTab(t.key); setRows([]); }}
                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                                tab === t.key
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ── RESTORAN ── */}
                {tab === 'orders' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <FiltersBar />
                        </div>
                        {loading ? (
                            <div className="p-12 text-center text-gray-400">Yüklənir...</div>
                        ) : rows.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">Nəticə tapılmadı.</div>
                        ) : (
                            <div className="overflow-x-auto"><table className="w-full min-w-[700px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Tarix</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Sifariş</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Masa</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Kassir</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Məbləğ</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Növ</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.payment_id} className="border-t border-gray-50 hover:bg-gray-50">
                                            <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{fmtDateTime(r.paid_at)}</td>
                                            <td className="px-4 py-3 text-sm font-semibold text-gray-800">#{r.order_num}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{r.table_name || '—'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{r.kassir_name || '—'}</td>
                                            <td className="px-4 py-3 text-sm font-mono font-bold text-gray-800">{fmt(r.amount)} ₼</td>
                                            <td className="px-4 py-3"><MethodBadge method={r.method} /></td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    {r.method !== 'hesab' && (
                                                        <button
                                                            onClick={() => openChange('orders', r)}
                                                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition-all"
                                                        >
                                                            💱 Dəyiş
                                                        </button>
                                                    )}
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => setRefundChoice({ orderId: r.order_id || r.order_num })}
                                                            disabled={refunding}
                                                            title="Geri qaytar — kassadan / kassasız"
                                                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all disabled:opacity-50"
                                                        >
                                                            ↩ Geri qaytar
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}
                    </div>
                )}

                {/* ── EVLƏR ── */}
                {tab === 'rentals' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <FiltersBar />
                        </div>
                        {loading ? (
                            <div className="p-12 text-center text-gray-400">Yüklənir...</div>
                        ) : rows.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">Nəticə tapılmadı.</div>
                        ) : (
                            <div className="overflow-x-auto"><table className="w-full min-w-[700px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Tarix</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qonaq</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ev</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qeyd</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Məbləğ</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Növ</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.id} className="border-t border-gray-50 hover:bg-gray-50">
                                            <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                                            <td className="px-4 py-3 text-sm font-semibold text-gray-800">{r.guest_name}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{r.house_name}</td>
                                            <td className="px-4 py-3 text-sm text-gray-400">{r.note || '—'}</td>
                                            <td className="px-4 py-3 text-sm font-mono font-bold text-gray-800">{fmt(r.amount)} ₼</td>
                                            <td className="px-4 py-3">
                                                <MethodBadge method={r.method} />
                                                {r.method === 'bolunmus' && (
                                                    <div className="mt-1 text-[11px] font-mono text-gray-500 whitespace-nowrap">
                                                        💵 {fmt(r.cash_amount)} · 💳 {fmt(r.card_amount)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => openChange('rentals', r)}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition-all"
                                                    >
                                                        💱 Dəyiş
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => doRentalRefund(r)}
                                                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all"
                                                        >
                                                            ↩ Geri qaytar
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}
                    </div>
                )}

                {/* ── SATIŞ ── */}
                {tab === 'satis' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <FiltersBar />
                        </div>
                        {loading ? (
                            <div className="p-12 text-center text-gray-400">Yüklənir...</div>
                        ) : rows.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">Nəticə tapılmadı.</div>
                        ) : (
                            <div className="overflow-x-auto"><table className="w-full min-w-[700px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Tarix</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Kateqoriya</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qeyd</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Nağd</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">POS</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Cəmi</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => {
                                        const isEditing = satisEdit?.id === r.id;
                                        return (
                                            <tr key={r.id} className={`border-t border-gray-50 transition-all ${isEditing ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                                <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{r.date}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{r.category_name || '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-400">{r.note || '—'}</td>
                                                <td className="px-4 py-3">
                                                    {isEditing ? (
                                                        <input
                                                            type="number" min="0" step="0.01"
                                                            value={satisEdit.nagd}
                                                            onChange={e => setSatisEdit({ ...satisEdit, nagd: e.target.value })}
                                                            className="w-24 px-2 py-1 border border-amber-300 rounded-lg text-sm font-mono focus:outline-none"
                                                        />
                                                    ) : (
                                                        <span className={`text-sm font-mono font-semibold ${Number(r.nagd_amount) > 0 ? 'text-emerald-700' : 'text-gray-300'}`}>
                                                            {fmt(r.nagd_amount)} ₼
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isEditing ? (
                                                        <input
                                                            type="number" min="0" step="0.01"
                                                            value={satisEdit.pos}
                                                            onChange={e => setSatisEdit({ ...satisEdit, pos: e.target.value })}
                                                            className="w-24 px-2 py-1 border border-amber-300 rounded-lg text-sm font-mono focus:outline-none"
                                                        />
                                                    ) : (
                                                        <span className={`text-sm font-mono font-semibold ${Number(r.pos_amount) > 0 ? 'text-blue-700' : 'text-gray-300'}`}>
                                                            {fmt(r.pos_amount)} ₼
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm font-mono font-bold text-gray-800">
                                                    {fmt(Number(r.nagd_amount) + Number(r.pos_amount))} ₼
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isEditing ? (
                                                        <div className="flex gap-1.5">
                                                            <button onClick={saveSatis} disabled={saving}
                                                                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-all">
                                                                {saving ? '...' : 'Saxla'}
                                                            </button>
                                                            <button onClick={() => setSatisEdit(null)}
                                                                className="px-2 py-1.5 rounded-lg text-xs text-gray-500 border border-gray-200 hover:bg-gray-50 transition-all">
                                                                İmtina
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setSatisEdit({ id: r.id, nagd: r.nagd_amount, pos: r.pos_amount })}
                                                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition-all"
                                                        >
                                                            ✏️ Düzəlt
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table></div>
                        )}
                    </div>
                )}

                {/* ── AXTARIŞ ── */}
                {tab === 'search' && (
                    <div>
                        {/* Search filters */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
                            <div className="flex flex-wrap gap-2">
                                <input
                                    type="text"
                                    value={searchQ}
                                    onChange={e => setSearchQ(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && doSearch()}
                                    placeholder="🔍 Sifariş № (#123), məbləğ, ad, masa, qonaq..."
                                    className="flex-1 min-w-48 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 transition-all"
                                />
                                <select value={searchMethod} onChange={e => setSearchMethod(e.target.value)} className={inputCls}>
                                    <option value="all">Bütün növlər</option>
                                    <option value="nagd">Nağd</option>
                                    <option value="kart">POS / Kart</option>
                                    {foodDelivery && <option value="delivery">Food delivery</option>}
                                </select>
                                <input type="date" value={searchFrom} onChange={e => setSearchFrom(e.target.value)}
                                    placeholder="Başlanğıc" className={inputCls} />
                                <input type="date" value={searchTo}   onChange={e => setSearchTo(e.target.value)}
                                    placeholder="Son" className={inputCls} />
                                <button onClick={doSearch} disabled={searching}
                                    className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all">
                                    {searching ? 'Axtarılır...' : '🔍 Axtar'}
                                </button>
                            </div>
                        </div>

                        {searchRows.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-700">{searchRows.length} nəticə</span>
                                    <span className="text-xs text-gray-400">— bütün mənbələrdə</span>
                                </div>
                                <div className="overflow-x-auto"><table className="w-full min-w-[700px]">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Tarix</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Mənbə</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Təsvir</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qeyd</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Məbləğ</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Növ</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchRows.map((r, i) => {
                                            const SOURCE_LABEL = {
                                                restoran: { label: '🍽️ Restoran', cls: 'bg-orange-100 text-orange-700' },
                                                evler:    { label: '🏠 Ev',        cls: 'bg-green-100 text-green-700'  },
                                                satis:    { label: '📦 Satış',     cls: 'bg-purple-100 text-purple-700' },
                                            };
                                            const src = SOURCE_LABEL[r.source] || { label: r.source, cls: 'bg-gray-100 text-gray-600' };
                                            return (
                                                <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${src.cls}`}>
                                                            {src.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{r.description}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-400">{r.note || '—'}</td>
                                                    <td className="px-4 py-3 text-sm font-mono font-bold text-gray-800">{fmt(r.amount)} ₼</td>
                                                    <td className="px-4 py-3"><MethodBadge method={r.method} /></td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-1.5">
                                                            {(r.source === 'restoran' || r.source === 'evler') && r.method !== 'hesab' && r.method !== 'mixed' && (
                                                                <button
                                                                    onClick={() => openChange(r.source === 'restoran' ? 'orders' : 'rentals', r.source === 'restoran' ? { payment_id: r.record_id, method: r.method } : { id: r.record_id, method: r.method })}
                                                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition-all"
                                                                >
                                                                    💱 Dəyiş
                                                                </button>
                                                            )}
                                                            {isAdmin && r.source === 'restoran' && (
                                                                <button
                                                                    onClick={() => setRefundChoice({ orderId: r.order_id })}
                                                                    disabled={refunding}
                                                                    title="Geri qaytar — kassadan / kassasız"
                                                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all disabled:opacity-50"
                                                                >
                                                                    ↩ Geri qaytar
                                                                </button>
                                                            )}
                                                            {isAdmin && r.source === 'evler' && (
                                                                <button
                                                                    onClick={() => doRentalRefund({ id: r.record_id, source: 'rental', guest_name: r.description, amount: r.amount })}
                                                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all"
                                                                >
                                                                    ↩ Geri qaytar
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table></div>
                            </div>
                        )}

                        {!searching && searchRows.length === 0 && searchQ === '' && (
                            <div className="py-16 text-center text-gray-400">
                                <div className="text-4xl mb-3">🔍</div>
                                <p className="text-sm">Restoran, ev ödənişləri və satış arasında nağd/POS əməliyyatları axtarın</p>
                            </div>
                        )}
                        {!searching && searchRows.length === 0 && searchQ !== '' && (
                            <div className="py-12 text-center text-gray-400 text-sm">Nəticə tapılmadı.</div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Change Method Modal ── */}
            {changeModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setChangeModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="font-bold text-gray-800 text-lg">💱 Ödəniş növünü dəyiş</h3>
                            <p className="text-xs text-gray-400 mt-1">
                                Hazırkı növ: <MethodBadge method={changeModal.record.method} />
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm font-semibold text-gray-600 mb-3">Yeni ödəniş növü:</p>
                            {['nagd', 'kart', ...(foodDelivery && changeModal.source === 'orders' ? ['delivery'] : [])].map(m => {
                                const isCurrent = m === changeModal.record.method;
                                return (
                                    <button
                                        key={m}
                                        type="button"
                                        disabled={isCurrent}
                                        onClick={() => !isCurrent && setNewMethod(m)}
                                        className={`w-full text-left px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                                            isCurrent
                                                ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                                                : newMethod === m
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}
                                    >
                                        {m === 'nagd' ? '💵 Nağd' : m === 'delivery' ? '🛵 Food delivery' : '💳 POS / Kart'}{isCurrent ? ' (hazırkı)' : ''}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="px-5 pb-5 flex gap-3">
                            <button type="button" onClick={() => setChangeModal(null)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">
                                İmtina
                            </button>
                            <button
                                type="button"
                                onClick={saveChange}
                                disabled={saving || newMethod === changeModal.record.method}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all"
                            >
                                {saving ? 'Saxlanılır...' : 'Dəyiş'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Refund — masa seçimi (orijinal masa doludursa) */}
            {/* Geri qaytar — kassadan / kassasız seçimi */}
            {refundChoice && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={e => e.target === e.currentTarget && setRefundChoice(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-1">↩ Sifariş #{refundChoice.orderId} — geri qaytar</h3>
                        <p className="text-sm text-gray-500 mb-4">Qaytarma necə olsun?</p>
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => { const oid = refundChoice.orderId; setRefundChoice(null); doRefundFiscal(oid); }}
                                className="w-full py-3 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 text-left px-4">
                                💳 Kassadan qaytar
                                <span className="block text-[11px] font-normal text-blue-100 mt-0.5">SmartOne qaytarma qəbzi + karta referenslı qaytarma, sonra sifariş geri açılır</span>
                            </button>
                            <button
                                onClick={() => { const oid = refundChoice.orderId; setRefundChoice(null); doRefund(oid); }}
                                className="w-full py-3 rounded-xl text-sm font-bold text-gray-800 bg-gray-100 hover:bg-gray-200 text-left px-4">
                                🧾 Kassasız qaytar
                                <span className="block text-[11px] font-normal text-gray-500 mt-0.5">Yalnız sistemdə geri açılır (kassa/terminal əməliyyatı yox)</span>
                            </button>
                            <button
                                onClick={() => setRefundChoice(null)}
                                className="w-full py-2.5 rounded-xl text-sm font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kassadan qaytarma prosesi — bloklayan popup */}
            {refundFiscal.open && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
                     onClick={e => { if (refundFiscal.closable && e.target === e.currentTarget) setRefundFiscal({ open: false, message: '', closable: false, error: false }); }}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 ${refundFiscal.error ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {refundFiscal.error ? '❌' : <span className="inline-block h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />}
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">{refundFiscal.title || '💳 Kassadan qaytarma'}</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{refundFiscal.message}</p>
                        {refundFiscal.closable ? (
                            <button onClick={() => setRefundFiscal({ open: false, message: '', closable: false, error: false })}
                                className="mt-5 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gray-600 hover:bg-gray-700">Bağla</button>
                        ) : (
                            <p className="mt-4 text-[11px] text-gray-400">Zəhmət olmasa gözləyin — prosesi dayandırmayın.</p>
                        )}
                    </div>
                </div>
            )}

            {tablePopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={e => e.target === e.currentTarget && setTablePopup(null)}>
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                        <div className="border-b border-gray-100 p-5">
                            <h3 className="text-lg font-bold text-gray-800">↩ Boş masa seç</h3>
                            <p className="mt-1 text-sm text-gray-400">Orijinal masa doludur. Sifariş #{tablePopup.orderId} hansı masaya açılsın?</p>
                        </div>
                        <div className="max-h-80 overflow-y-auto p-4">
                            {tablePopup.tables.length === 0 ? (
                                <p className="py-6 text-center text-sm text-gray-400">Boş masa yoxdur.</p>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {tablePopup.tables.map(t => (
                                        <button key={t.id} type="button"
                                            disabled={refunding}
                                            onClick={() => doRefund(tablePopup.orderId, t.id)}
                                            className="rounded-xl border-2 border-gray-200 px-3 py-3 text-sm font-bold text-gray-700 hover:border-red-400 hover:bg-red-50 transition-all disabled:opacity-50">
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end border-t border-gray-100 p-4">
                            <button type="button" onClick={() => setTablePopup(null)}
                                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
